import type { FC } from 'react';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { PolarAngleAxis, PolarGrid, PolarRadiusAxis, Radar, RadarChart, Tooltip } from 'recharts';
import classNames from 'classnames';

import ThemeContext from 'app/common/ThemeContext';
import { RADAR_CHART_DATA_ORDER } from 'app/config/videogameSpecific.config';
import { sortAsModel } from 'app/utils/array';
import { round } from 'app/utils/number';
import { isNumber } from 'app/utils/types';
import { getValueFromScale } from 'app/components/Blocks/TeamsStatsBlock/LolTeamStats/utils';

import style from './style.module.scss';

interface Props extends ClassNameProp {
  data: TeamsStatsBlockMatch;
  chartClassName?: string;
}

const LolRadarChart: FC<Props> = ({ data, className, chartClassName }) => {
  const { t } = useTranslation();
  const { theme } = useContext(ThemeContext);

  const opponent1 = data.opponents.at(0);
  const opponent2 = data.opponents.at(1);

  const opponent1Stats = opponent1?.stats as StatsLol | undefined;
  const opponent2Stats = opponent2?.stats as StatsLol | undefined;

  const radarChartData: RadarChartData<PlaystyleChart>[] = sortAsModel(
    Object.entries(data?.playstyle_chart ?? []).map(([key, scale]) => {
      // Because typescript doesn't infer key type in Object.entries()
      const typeInferedKey = key as keyof StatsLolInChart;

      const opponent1Stat = opponent1Stats?.[typeInferedKey];
      const opponent2Stat = opponent2Stats?.[typeInferedKey];

      const reevaluatedScale: RadarChartMinMax = {
        min: Math.min(...[opponent1Stat, opponent2Stat, scale.min].filter(isNumber)),
        max: Math.max(...[opponent1Stat, opponent2Stat, scale.max].filter(isNumber))
      };

      return {
        name: typeInferedKey,
        team1: getValueFromScale(opponent1Stat, reevaluatedScale),
        team2: getValueFromScale(opponent2Stat, reevaluatedScale)
      };
    }),
    RADAR_CHART_DATA_ORDER,
    data => data.name
  );

  return (
    <div className={className}>
      <div className={style.chartTeams}>
        <div className={style.chartTeam}>
          <div className={style.chartTeamColor1} />
          <span>{opponent1?.name}</span>
        </div>
        <div className={style.chartTeam}>
          <div className={style.chartTeamColor2} />
          <span>{opponent2?.name}</span>
        </div>
      </div>
      {radarChartData.length > 2 && (
        <RadarChart
          height={175}
          width={290}
          outerRadius={70}
          data={radarChartData}
          startAngle={72}
          endAngle={-288}
          className={classNames(style.chartContainer, chartClassName)}
        >
          <PolarGrid radialLines={false} stroke={theme.global_border} />
          <PolarAngleAxis
            dataKey="name"
            radius={70}
            style={{ textTransform: 'uppercase', letterSpacing: 0.2 }}
            tick={{ fill: theme.text_secondary, fontWeight: 800, fontSize: 10 }}
            tickFormatter={name => t(`blocks_teams-stats_videogame_lol_radar_${name}`)}
          />
          <Radar
            name={opponent1?.name}
            dataKey="team1"
            stroke={theme.component_radar_team_1}
            fill={theme.component_radar_team_1}
            fillOpacity={0.4}
          />
          <PolarRadiusAxis angle={0} domain={[0, 100]} style={{ display: 'none' }} />
          <Radar
            name={opponent2?.name}
            dataKey="team2"
            stroke={theme.component_radar_team_2}
            fill={theme.component_radar_team_2}
            fillOpacity={0.4}
          />
          <Tooltip
            formatter={(_, __: string, props): string | number => {
              const opponent = props.dataKey === 'team1' ? opponent1Stats : opponent2Stats;
              const name = props.payload.name as keyof StatsLol;
              if (!opponent?.[name]) {
                return '-';
              }
              return name.startsWith('first_')
                ? `${round((opponent[name] as number) * 100, 1)}%`
                : (opponent[name] as number);
            }}
            labelFormatter={name => t(`blocks_teams-stats_videogame_lol_radar_${name}`)}
            wrapperClassName={style.chartTooltip}
          />
        </RadarChart>
      )}
    </div>
  );
};

LolRadarChart.displayName = 'LolRadarChart';

export default LolRadarChart;
